import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    ConfiguratorGroupDto,
    ConfiguratorPresetGroupDto,
    ConfiguratorRoomDto,
    configuratorKeys,
} from '@/services/configurator';
import { BuyerProjectDeadline } from '@/types';
import { useOptionConfigurator } from '@/components/OptionConfigurator/OptionConfiguratorContext';
import { getRoomDisplayName } from '../utils/configuratorRooms';

export interface PresetGroupWithContext {
    group: ConfiguratorPresetGroupDto;
    /** Resolved deadline ID for the v2 pickStyle/unpickStyle call. Falls back to 0 when no option references the preset's styles. */
    deadlineId: number;
    /** Resolved room ID (0 = "general") if the group is room-scoped. */
    roomId: number | null;
}

export interface OptionsFilterFacets {
    /** Deadline ids that have at least one option group in the v2 data. */
    deadlineIds: number[];
    /** Rooms that have at least one group; id 0 falls back to "General". */
    rooms: Array<{ id: number; name: string }>;
    /** Non-room project tags present on any visible group. */
    tags: Array<{ id: number; name: string }>;
    /** Item category ids present in any option's product. */
    itemCategoryIds: number[];
}

export interface UseOptionsDataResult {
    residentialUnitId: number;
    isLoading: boolean;
    /** Room-grouped view (reconstructed from the flat groups). Null until first load. */
    rooms: ConfiguratorRoomDto[] | null;
    /** Project deadlines (from scope.projectData). */
    deadlines: BuyerProjectDeadline[];
    /** Preset (style) groups paired with their derived deadlineId/roomId. */
    presetGroups: PresetGroupWithContext[];
    /** Lookup of a group by (groupId, roomId). v2 allows the same groupId across multiple rooms. */
    findGroup: (groupId: number, roomId: number) => ConfiguratorGroupDto | undefined;
    /** Flat list of every v2 option group (used by dependency walking). */
    allGroups: ConfiguratorGroupDto[];
    /** Filter facets derived from the v2 data we actually have on screen. */
    filterFacets: OptionsFilterFacets;
    refetch: () => Promise<unknown>;
}

function roomFacetName(room: ConfiguratorRoomDto): string {
    return getRoomDisplayName(room) || `Room ${room.roomId}`;
}

export function useOptionsData(): UseOptionsDataResult {
    // Data now flows through the shared OptionConfiguratorContext (provided by
    // OptionConfiguratorProvider in the authenticated flow, or ShowroomConfiguratorProvider in the
    // public showroom). The legacy page shows every deadline at once, so it reads the unfiltered
    // `allDeadlinesConfiguratorData` rather than the single-deadline `configuratorData`.
    const { residentialUnitId, projectData, allDeadlinesConfiguratorData, allDeadlineRooms } = useOptionConfigurator();
    const queryClient = useQueryClient();
    const deadlines = useMemo<BuyerProjectDeadline[]>(() => projectData?.deadlines ?? [], [projectData]);

    const data = allDeadlinesConfiguratorData;
    const isLoading = !data;
    // Mutations update the shared cache themselves; refetch just invalidates the authenticated
    // query (a no-op in the showroom, whose data lives under a different key).
    const refetch = useCallback(
        () => queryClient.invalidateQueries({ queryKey: configuratorKeys.allDeadlinesByUnit(residentialUnitId) }),
        [queryClient, residentialUnitId]
    );

    const allGroups = useMemo<ConfiguratorGroupDto[]>(() => data?.groups ?? [], [data]);

    // Room-grouped view comes from the shared context (`allDeadlineRooms`), which both providers
    // derive from the same flat groups — the auth provider via the buyer rooms endpoint, the
    // showroom provider via its anonymous payload. Sourcing it here keeps the showroom working
    // without calling the authenticated useBuyerRoomMap.
    const rooms = useMemo<ConfiguratorRoomDto[] | null>(
        () => (data ? allDeadlineRooms : null),
        [data, allDeadlineRooms]
    );

    const groupByGroupAndRoom = useMemo(() => {
        const map = new Map<string, ConfiguratorGroupDto>();
        for (const group of allGroups) {
            map.set(`${group.id}:${group.roomId ?? 0}`, group);
        }
        return map;
    }, [allGroups]);

    const findGroup = useMemo(
        () => (groupId: number, roomId: number) => groupByGroupAndRoom.get(`${groupId}:${roomId}`),
        [groupByGroupAndRoom]
    );

    const presetGroups = useMemo<PresetGroupWithContext[]>(() => {
        if (!data) return [];
        // v2 pickStyle/unpickStyle need a deadlineId. Preset groups don't carry one,
        // so we infer by scanning options that reference any style id in the group.
        const styleIdToDeadline = new Map<number, number>();
        for (const og of data.groups ?? []) {
            for (const opt of og.options) {
                for (const p of opt.presets ?? []) {
                    if (!styleIdToDeadline.has(p.presetId)) {
                        styleIdToDeadline.set(p.presetId, og.deadlineId);
                    }
                }
            }
        }
        return (data.styleGroups ?? []).map((g) => {
            const deadlineId = g.styles.reduce<number>((acc, s) => acc || styleIdToDeadline.get(s.id) || 0, 0);
            return { group: g, deadlineId, roomId: g.roomId };
        });
    }, [data]);

    const filterFacets = useMemo<OptionsFilterFacets>(() => {
        if (!rooms) {
            return { deadlineIds: [], rooms: [], tags: [], itemCategoryIds: [] };
        }
        const deadlineIds = new Set<number>();
        const itemCategoryIds = new Set<number>();
        const roomFacets: Array<{ id: number; name: string }> = [];
        const tagsById = new Map<number, { name: string; sortOrder: number }>();
        for (const room of rooms) {
            if (room.groups.length === 0) continue;
            // Skip the synthetic "General" bucket (roomId 0, groups with no room) — it isn't a
            // real room, so we don't offer it as a filter choice. Its groups still appear in the
            // list and surface via tag filters.
            if (room.roomId !== 0) roomFacets.push({ id: room.roomId, name: roomFacetName(room) });
            for (const group of room.groups) {
                deadlineIds.add(group.deadlineId);
                for (const tag of group.tags) {
                    if (!tagsById.has(tag.id)) tagsById.set(tag.id, { name: tag.name, sortOrder: tag.sortOrder });
                }
                for (const opt of group.options) {
                    const catId = opt.product?.itemCategoryId;
                    if (catId && catId > 0) itemCategoryIds.add(catId);
                }
            }
        }
        // Order tags by the project-defined sortOrder (Settings > Tags) rather than by the order
        // groups happen to reference them, so the filter matches what the client configured.
        const tags = Array.from(tagsById, ([id, { name, sortOrder }]) => ({ id, name, sortOrder }))
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(({ id, name }) => ({ id, name }));
        return {
            deadlineIds: Array.from(deadlineIds),
            rooms: roomFacets,
            tags,
            itemCategoryIds: Array.from(itemCategoryIds),
        };
    }, [rooms]);

    return {
        residentialUnitId,
        isLoading,
        rooms,
        deadlines,
        presetGroups,
        findGroup,
        allGroups,
        filterFacets,
        refetch,
    };
}
